import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Container, 
  Paper, 
  Link as MuiLink, 
  Alert, 
  Snackbar,
  IconButton, 
  InputAdornment,
  FormControl,
  Checkbox,
  FormControlLabel,
  Divider,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';

// Email validation
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Show Snackbar whenever error changes to a non-empty string
  useEffect(() => {
    if (error) {
      setShowSnackbar(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowSnackbar(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for redirect message from protected routes
  useEffect(() => {
    if (location.state?.from) {
      setError('Please sign in to access this page');
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      setIsSubmitting(true);
      
      // Attempt to sign in with provided credentials
      const result = await signIn(email, password, rememberMe);
      console.log('Sign in result:', result);
      
      // Check for authentication errors
      if (result.error) {
        // Set a more user-friendly error message based on the error type
        const errorMsg = result.error.message || 'Unknown error';
        console.log('Authentication error message:', errorMsg);
        
        if (errorMsg.includes('Invalid login credentials') || errorMsg.includes('Invalid credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (errorMsg.includes('Email not confirmed')) {
          setError('Please verify your email before logging in.');
        } else {
          setError(errorMsg || 'Failed to log in. Please try again.');
        }
        
        // Explicitly show the snackbar
        setShowSnackbar(true);
        
        // Log the error for debugging
        console.error('Login failed:', result.error);
        return;
      }
      
      // Always navigate to dashboard after successful login
      navigate('/dashboard', { replace: true });
      
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to log in. Please try again.');
      setShowSnackbar(true);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Snackbar
        open={showSnackbar && !!error}
        autoHideDuration={5000}
        onClose={() => { setShowSnackbar(false); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ maxWidth: '80%' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="error"
          variant="filled"
          elevation={6}
          sx={{ 
            width: '100%',
            fontWeight: 'medium',
            '& .MuiAlert-icon': { fontSize: '1.25rem' }
          }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Paper 
        elevation={3} 
        sx={{ 
          mt: { xs: 4, sm: 8 }, 
          p: { xs: 3, sm: 4 },
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          borderRadius: 2,
          boxShadow: '0 8px 16px rgba(0,0,0,0.05)'
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography component="h1" variant="h5" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            Strawberry Growth Tracker
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Sign in to your account
          </Typography>
        </Box>
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              width: '100%', 
              mb: 3, 
              borderRadius: 1,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
        
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            width: '100%',
            '& .MuiTextField-root': { mb: 2 }
          }}
          noValidate
        >
          <FormControl fullWidth error={!!fieldErrors.email}>
            <TextField
              margin="normal"
              required
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) {
                  setFieldErrors(prev => ({ ...prev, email: '' }));
                }
              }}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              disabled={isSubmitting}
            />
          </FormControl>
          
          <FormControl fullWidth error={!!fieldErrors.password}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) {
                  setFieldErrors(prev => ({ ...prev, password: '' }));
                }
              }}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  disabled={isSubmitting}
                />
              }
              label="Remember me"
              sx={{ color: 'text.secondary' }}
            />
            <MuiLink 
              component={Link} 
              to="/forgot-password" 
              variant="body2"
              sx={{ textDecoration: 'none' }}
            >
              Forgot password?
            </MuiLink>
          </Box>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            sx={{ 
              py: 1.5,
              mb: 2,
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 1,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">OR</Typography>
          </Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <MuiLink 
                component={Link} 
                to="/register" 
                variant="body2"
                sx={{ fontWeight: 500 }}
              >
                Sign Up
              </MuiLink>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
